import React from 'react';
import { isPlaceholderPlace } from '../utils/placeLabel';

/**
 * The invitee band on a Signal card — design handoff "Signal Card — Invitees",
 * direction 1B, with the header cell left alone (OPEN / CURATED stays; the
 * audience chip lives in this band instead).
 *
 * Two shapes, because two kinds of signal:
 *   named room (friends, close friends, hand-picked, club) → chip + faces
 *   open room  (public, city portal)                       → reach text + who's in
 *
 * City portal is not a separate audience. A signal reaches a town's square by
 * LOCATION — SquareService picks up any live public signal whose location_city
 * matches the town — so it is a public signal that isn't international, and the
 * chip is derived here rather than stored.
 */

const PASTELS = ['bg-hand-pink', 'bg-hand-blue', 'bg-hand-lavender', 'bg-hand-cream'];

/** Two-letter initials, matching AddPeopleModal's placeholder tiles. */
export function initialsFor(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Which room this went to, and how its chip reads.
 * Widest room wins when a signal was sent to several at once.
 */
export function audienceChip(signal) {
  const rooms = signal.audiences || (signal.signalAudience ? [signal.signalAudience] : []);
  const has = (r) => rooms.includes(r);

  if (has('public')) {
    // International public = the whole network. Anything narrower is reaching one
    // town, which is exactly what the City Portal surface shows.
    return signal.signalRadius === 'international'
      ? { key: 'public', label: 'Public', fill: 'bg-white', open: true }
      : { key: 'town_square', label: 'City portal', fill: 'bg-hand-lavender', open: true };
  }
  if (has('club')) {
    return {
      key: 'club',
      label: signal.clubName ? `Club · ${signal.clubName}` : 'Club',
      fill: 'bg-black',
      text: 'text-white',
      open: false,
    };
  }
  if (has('friends')) return { key: 'friends', label: 'Friends', fill: 'bg-hand-blue', open: false };
  if (has('close_friends')) return { key: 'close_friends', label: 'Close friends', fill: 'bg-hand-pink', open: false };
  if (has('select')) return { key: 'hand_picked', label: 'Hand-picked', fill: 'bg-hand-cream', open: false };
  return null;
}

/** "ANYONE IN PENTICTON · 3 IN" for the open rooms. */
export function reachLabel(signal) {
  const where = signal.primaryCityLabel || signal.locationLabel;
  const scope = !isPlaceholderPlace(where) ? `Anyone in ${where}` : 'Anyone nearby';
  const inCount = signal.acceptedCount || 0;
  return inCount > 0 ? `${scope} · ${inCount} in` : scope;
}

/**
 * Overlapping square tiles. Every tile covered by the next one carries
 * padding-right so its centred initials don't read off-centre under the overlap,
 * and a 2px white offset so two black borders never merge into one thick rule.
 */
/**
 * Who this is, on hover. Desktop only — hover does not exist on touch, and the
 * whole stack is tappable there instead, so nothing is unreachable.
 */
export function InviteeHoverCard({ person }) {
  return (
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-56 flex-col gap-1.5 border-2 border-black bg-white p-3 text-left opacity-0 shadow-none transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 sm:flex"
    >
      <span className="flex items-center gap-2">
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden border-2 border-black bg-hand-lavender font-mono text-[11px] font-bold text-black"
        >
          {person.avatarUrl
            ? <img src={person.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
            : initialsFor(person.displayName)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-bold leading-tight text-black">{person.displayName}</span>
          {person.handle && (
            <span className="block truncate font-mono text-[10px] uppercase tracking-[0.08em] text-hand-muted">@{person.handle}</span>
          )}
        </span>
      </span>
      {person.location && (
        <span className="truncate font-mono text-[10px] uppercase tracking-[0.08em] text-hand-muted">{person.location}</span>
      )}
      {person.vibeCheck && (
        <span className="line-clamp-3 text-[12px] leading-snug text-black">“{person.vibeCheck}”</span>
      )}
    </span>
  );
}

export function AvatarStack({ people, overflow = 0, showYou = false, size = 34, onOpenList }) {
  const pad = size >= 34 ? 9 : 8;
  const tiles = [];

  if (showYou) {
    tiles.push(
      <span
        key="you"
        className="flex flex-shrink-0 items-center justify-center overflow-hidden border-2 border-black bg-hand-cyan font-mono font-bold text-black"
        style={{ width: size, height: size, fontSize: 9 }}
      >
        You
      </span>,
    );
  }

  people.forEach((p, i) => {
    tiles.push(
      <span
        key={p.userId || `p${i}`}
        className="group relative flex flex-shrink-0"
        style={{ width: size, height: size }}
      >
        <span
          title={p.displayName}
          className={`flex h-full w-full items-center justify-center overflow-hidden border-2 border-black font-mono font-bold text-black ${p.avatarUrl ? 'bg-white' : PASTELS[i % PASTELS.length]}`}
          style={{ fontSize: size >= 34 ? 12 : 11 }}
        >
          {p.avatarUrl
            ? <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
            : initialsFor(p.displayName)}
        </span>
        <InviteeHoverCard person={p} />
      </span>,
    );
  });

  if (overflow > 0) {
    tiles.push(
      <button
        key="overflow"
        type="button"
        onClick={(ev) => { ev.stopPropagation(); onOpenList?.(); }}
        aria-label={`See all invited — ${overflow} more`}
        className="flex flex-shrink-0 items-center justify-center border-2 border-black bg-black font-mono font-bold text-white transition-colors hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
        style={{ width: size, height: size, fontSize: size >= 34 ? 12 : 11 }}
      >
        +{overflow}
      </button>,
    );
  }

  // The whole stack is one target, per the handoff: tapping any face opens the
  // list rather than that person's profile. Individual tiles are deliberately not
  // separate links — the hover card already identifies who they are, and making
  // each face its own destination fights the card's own click behaviour.
  const stack = (
    <span className="flex items-center">
      {tiles.map((tile, i) => {
        const covered = i < tiles.length - 1; // the top-most tile is never clipped
        return React.cloneElement(tile, {
          style: {
            ...tile.props.style,
            marginLeft: i === 0 ? 0 : -pad,
            boxSizing: 'border-box',
            paddingRight: covered ? pad : 0,
            boxShadow: i === 0 ? undefined : `-2px 0 0 0 #fff`,
          },
        });
      })}
    </span>
  );

  if (!onOpenList) return stack;

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(ev) => { ev.stopPropagation(); onOpenList(); }}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); onOpenList(); }
      }}
      aria-label="See everyone invited"
      className="flex cursor-pointer items-center focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
    >
      {stack}
    </span>
  );
}
